import { Controller } from "stimulus"
import Chart from "chart.js/auto"

const POLL_MS = 5000
const MAX_FAILURES = 5

// Above this many events in the last 30 minutes (~167/min, comfortably above
// the documented p99 of 103/min) a tick risks the companies CTE's Seq Scan
// cost measured at the 3,054/min peak (391ms, ~890MB of buffer traffic).
// This app runs ONE Puma process with FIVE threads for all public traffic,
// so above the threshold we back off to a slower cadence.
const HIGH_VOLUME_THRESHOLD = 5000
const HIGH_VOLUME_POLL_MS = 15000

// Used both where the map failure is first detected and wherever the banner
// might need to be re-asserted afterwards, so the two can never drift apart.
const MAP_UNAVAILABLE_MESSAGE = "Map unavailable — other panels still live"

// A muted dark basemap (Map constructor `styles`, which works WITHOUT a Map
// ID — only AdvancedMarkerElement needs one, and this app has no provisioned
// Map ID, see CityMarkerOverlay below). Dark background, dimmed roads, quiet
// labels, POI clutter fully suppressed so city dots read as the signal.
// Defined as a named constant, as data, so it is obvious and easy to swap.
const DARK_MAP_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1d2c4d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8ec3b9" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a3646" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "administrative.land_parcel", elementType: "labels.text.fill", stylers: [{ color: "#64779e" }] },
  { featureType: "administrative.province", elementType: "geometry.stroke", stylers: [{ color: "#4b6878" }] },
  { featureType: "landscape.man_made", elementType: "geometry.stroke", stylers: [{ color: "#334e87" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#023e58" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry.fill", stylers: [{ color: "#023e58" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#304a7d" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#98a5be" }] },
  { featureType: "road", elementType: "labels.text.stroke", stylers: [{ color: "#1d2c4d" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2c6675" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#255763" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#b0d5ce" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e1626" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#4e6d70" }] },
]

// #2563eb (blue-600) was tuned for a light/plain basemap and loses contrast
// against DARK_MAP_STYLE's near-navy background; blue-400 stays legible.
const MARKER_COLOR = "#60a5fa"

// At the documented 3,054 events/min peak many cities can increase in one
// tick; starting one ripple per city would mean up to ~200 concurrent
// animations. Cap it to the N cities with the largest increase so the eye is
// drawn to where the biggest jump just happened, not everywhere at once.
const RIPPLE_CAP = 20

// One DOM node per city, so each can be animated with CSS keyframes (the
// compositor-only path for `transform`/`opacity`) instead of via
// marker.setIcon() on a timer, which at up to 200 cities would be heavy
// main-thread work every frame. This app has no provisioned Google Cloud Map
// ID (see benefits_map_controller.js's `mapId: "map"` placeholder), so
// AdvancedMarkerElement is not an option here — a plain OverlayView renders
// real elements without requiring one.
// Created lazily, NOT declared at module scope. `extends google.maps.OverlayView`
// is evaluated the moment the class declaration is, and this file is required
// by controllers/index.js on EVERY page through
// require.context(..., /_controller\.(js|ts)$/). On any page that does not load
// the Maps API -- which is every page but this dashboard -- `google` is not
// merely undefined, it is an unresolvable identifier, so that threw a
// ReferenceError out of definitionsFromContext and application.load() never
// ran. The result was that NO Stimulus controller registered anywhere in the
// app: the employee list Actions menu did nothing, the OTP form posted an
// empty code, and the Discover carousels never initialised.
//
// Building it inside a function defers that reference to the first marker,
// which can only happen once the map exists. Memoised so every marker shares
// one class.
let cityMarkerOverlayClass = null

function cityMarkerOverlay() {
  if (cityMarkerOverlayClass) { return cityMarkerOverlayClass }

  cityMarkerOverlayClass = class extends google.maps.OverlayView {
    constructor(position, diameter, color, title) {
      super()
      this.position = position
      this.diameter = diameter
      this.color = color
      this.title = title
      this.div = null
      this.ripple = null
    }

    onAdd() {
      const div = document.createElement("div")
      div.className = "live-map-marker"
      div.style.setProperty("--marker-color", this.color)
      div.title = this.title // property assignment, not innerHTML — same XSS rule as the rest of this file

      const pulse = document.createElement("div")
      pulse.className = "live-map-marker__pulse"
      const dot = document.createElement("div")
      dot.className = "live-map-marker__dot"
      const ripple = document.createElement("div")
      ripple.className = "live-map-marker__ripple"
      // Clean up after itself so the same fixed class can retrigger the
      // animation on the next increase (classList.add on an already-present
      // class is a no-op and would never re-fire).
      ripple.addEventListener("animationend", () => {
        ripple.classList.remove("live-map-marker__ripple--active")
      })

      div.append(pulse, dot, ripple)
      this.div = div
      this.ripple = ripple
      this.getPanes().overlayLayer.appendChild(div)
    }

    draw() {
      if (!this.div) return
      const projection = this.getProjection()
      if (!projection) return
      const point = projection.fromLatLngToDivPixel(this.position)
      if (!point) return
      const radius = this.diameter / 2
      this.div.style.width = `${this.diameter}px`
      this.div.style.height = `${this.diameter}px`
      this.div.style.left = `${point.x - radius}px`
      this.div.style.top = `${point.y - radius}px`
    }

    onRemove() {
      if (this.div && this.div.parentNode) this.div.parentNode.removeChild(this.div)
      this.div = null
      this.ripple = null
    }

    // Position AND size must both be updated on the existing entry (city
    // lat/lng are a rolling average and drift tick to tick), and draw() must
    // be called explicitly: the Maps API only invokes it on add or on a map
    // viewport change, never on our own data changes.
    update(position, diameter, color, title) {
      this.position = position
      this.diameter = diameter
      this.color = color
      this.title = title
      if (this.div) {
        this.div.style.setProperty("--marker-color", color)
        this.div.title = title
      }
      this.draw()
    }

    triggerRipple() {
      if (!this.ripple) return
      this.ripple.classList.remove("live-map-marker__ripple--active")
      void this.ripple.offsetWidth // force reflow so re-adding the class restarts the animation
      this.ripple.classList.add("live-map-marker__ripple--active")
    }
  }

  return cityMarkerOverlayClass
}

export default class extends Controller {
  static values = { url: String }
  static targets = ["banner", "last5", "last30", "last30People", "last30Baseline",
                    "botsExcluded", "chart", "failures", "companies", "feed",
                    "modal", "modalMeta", "modalLines",
                    "zeroResultRate", "zeroResultCaveat", "zeroResultQueries",
                    "otpCauseCaveat", "otpCauses"]

  connect() {
    this.markers = new Map()
    this.failureCount = 0
    this.mapUnavailable = false
    this.lastTickAt = 0

    // A blocked/missing Maps script (bad API key, quota, referrer
    // restriction) must not take the whole dashboard down with it — the
    // counters, chart and lists are independent of the map and must keep
    // working, with a clear banner instead of a silently blank page.
    try {
      this.map = new google.maps.Map(document.getElementById("live-map"), {
        zoom: 5, center: { lat: 25.0, lng: 52.0 }, styles: DARK_MAP_STYLE,
      })
    } catch (error) {
      this.map = null
      this.mapUnavailable = true
      this.showBanner(MAP_UNAVAILABLE_MESSAGE)
    }

    this.chart = new Chart(this.chartTarget, {
      type: "bar",
      data: { labels: [], datasets: [{ data: [], backgroundColor: "#2563eb" }] },
      // maintainAspectRatio defaults to true in chart.js 3.6.2, which ignores
      // the container's height and derives one from the canvas width instead
      // (initCanvas sets canvas.height = canvas.width / 2), overflowing the
      // 200px wrapper and painting over the panels below it.
      options: { animation: false, maintainAspectRatio: false, plugins: { legend: { display: false } } },
    })

    // Pause polling while the tab is hidden, resume (with an immediate tick,
    // so the view is not stale) when it becomes visible again. The realistic
    // failure mode is an admin leaving the tab open all day, so a hidden tab
    // must cost nothing.
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)

    // Escape must close the modal wherever focus is, which needs a
    // document-level listener (not a Stimulus data-action on the modal
    // itself, which only hears keys while something inside it is focused).
    // Same leak risk as visibilitychange above, same fix: bind once here,
    // remove in disconnect().
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)

    this.tick()
    this.scheduleNext(POLL_MS)
  }

  // Without this a leaked timer keeps polling, and a leaked listener keeps
  // firing, after a Turbo navigation.
  disconnect() {
    clearTimeout(this.timer)
    document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    document.removeEventListener("keydown", this.boundKeydown)
    if (this.chart) this.chart.destroy()
    // Each CityMarkerOverlay owns a DOM node appended to the map's own
    // overlay pane (not this controller's element), so a Turbo navigation
    // that tears down this element would otherwise leave every marker's node
    // attached to the map forever. setMap(null) is the OverlayView
    // equivalent of marker.setMap(null) and triggers onRemove.
    if (this.markers) {
      this.markers.forEach((overlay) => overlay.setMap(null))
      this.markers.clear()
    }
  }

  handleVisibilityChange() {
    clearTimeout(this.timer)
    if (document.hidden) return
    // Guard against a burst of rapid tab/workspace switches each firing an
    // immediate tick against the same 5-thread Puma process. Only tick right
    // away if the page has genuinely been idle longer than one poll
    // interval; otherwise wait out the remainder of that interval.
    const elapsed = Date.now() - (this.lastTickAt || 0)
    if (elapsed < POLL_MS) {
      this.scheduleNext(POLL_MS - elapsed)
    } else {
      this.tick()
    }
  }

  // Self-rescheduling setTimeout (rather than a fixed setInterval) so the
  // delay can change tick to tick based on volume, and so a hidden tab can
  // simply decline to arm the next timer at all.
  scheduleNext(delay) {
    clearTimeout(this.timer)
    if (document.hidden) return
    this.timer = setTimeout(() => this.tick(), delay)
  }

  async tick() {
    this.lastTickAt = Date.now()
    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
      // check_company_vpn renders plain text, not JSON, so never assume a body shape.
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const payload = await response.json()
      this.failureCount = 0
      if (payload.status === "index_pending") {
        this.showBanner("Building the activities index — live data is paused until it completes.")
        this.scheduleNext(POLL_MS)
        return
      }
      this.hideBanner()
      this.render(payload)
      const last30 = (payload.totals || {}).last30 || 0
      this.scheduleNext(last30 > HIGH_VOLUME_THRESHOLD ? HIGH_VOLUME_POLL_MS : POLL_MS)
    } catch (error) {
      this.failureCount += 1
      // Back off rather than stop: MAX_FAILURES * POLL_MS (25s) is SHORTER
      // than a Puma restart, so a permanent stop would freeze every open
      // dashboard across every deploy, recoverable only by a manual reload.
      // Keep retrying forever at a capped exponential delay instead;
      // failureCount resets to 0 on the next success (above), so a
      // transient blip returns to the normal cadence immediately once it
      // clears.
      if (this.failureCount >= MAX_FAILURES) {
        // Append the underlying status (e.g. "HTTP 403" / "HTTP 500") so an
        // auth problem is not misdiagnosed as a VPN problem.
        this.showBanner(`Live updates degraded (${error.message}). Retrying automatically — check you are on the company VPN.`)
        this.scheduleNext(Math.min(POLL_MS * Math.pow(2, this.failureCount), 60000))
        return
      }
      this.scheduleNext(POLL_MS)
    }
  }

  render(payload) {
    const totals = payload.totals || {}
    this.last5Target.textContent = totals.last5 || 0
    this.last30Target.textContent = totals.last30 || 0
    // `totals.people` is count(DISTINCT employee_id) over the same 30-minute,
    // bot-filtered window as `last30` -- a different question from
    // `totals.attributed` (events that merely carry an employee_id). Reading
    // the two together is the point: 361 events from 10 people reads very
    // differently from 361 events from 300 people, and that gap is itself
    // diagnostic. textContent only, per this file's existing XSS rule.
    this.last30PeopleTarget.textContent = `${totals.last30 || 0} events · ${totals.people || 0} people`
    this.renderBaseline(payload.baseline, totals)
    // `totals.bots_excluded` is the window's count(*) FILTER (WHERE is_bot) --
    // surfaced so the read-side bot filter can never silently hide what it
    // is excluding. textContent only, per this file's existing XSS rule.
    this.botsExcludedTarget.textContent = `${totals.bots_excluded || 0} bots excluded`

    this.chart.data.labels = (payload.per_minute || []).map((row) => row.m)
    this.chart.data.datasets[0].data = (payload.per_minute || []).map((row) => row.c)
    this.chart.update()

    this.syncMarkers(payload.cities || [])

    this.renderList(this.failuresTarget, payload.failures || [],
      (row) => `${row.activity_type} — ${row.parameters || "–"} (${row.c})`)

    const unattributed = (totals.last30 || 0) - (totals.attributed || 0)
    this.renderList(this.companiesTarget, payload.companies || [],
      (row) => `${row.company_name} (${row.c})`, `Unattributed (${unattributed})`)

    this.renderFeed(payload.feed || [])

    this.renderZeroResultSearches(payload.zero_result_searches)
    this.renderOtpCauses(payload.otp_causes)
  }

  // Zero-result searches cover the PAST 7 DAYS, unlike every other panel on
  // this page which reads the 30-minute `w` window -- see
  // LiveActivitySnapshot.zero_result_searches, which runs its own query
  // behind its own 5-minute cache (a THIRD cache, independent of the 4s
  // payload cache and the 5-minute baseline cache already on this page).
  // `benefit_search` was only instrumented on 2026-10-14, so "past 7 days"
  // is honestly a partial sample until a full week has accumulated. Rather
  // than silently presenting that partial sample as if it were a settled
  // 7-day trend, the sample size is stated right alongside the rate (the
  // chosen honest-labelling approach -- see the service's own comment),
  // with the earliest recorded search also shown for precision.
  renderZeroResultSearches(data) {
    const totals = (data || {}).totals || {}
    const total = totals.total_searches || 0
    const zero = totals.zero_result_searches || 0

    if (total === 0) {
      this.zeroResultRateTarget.textContent = "No searches recorded yet"
      this.zeroResultCaveatTarget.textContent = ""
    } else {
      const rate = Math.round((zero / total) * 100)
      this.zeroResultRateTarget.textContent = `${zero} of ${total} searches returned nothing (${rate}%)`
      this.zeroResultCaveatTarget.textContent =
        `Sample: ${total} searches, earliest recorded ${totals.earliest_search_at || "unknown"}. ` +
        "benefit_search tracking began 2026-10-14 -- this is a partial week, not yet a full 7-day trend."
    }

    this.renderList(this.zeroResultQueriesTarget, (data || {}).zero_result_queries || [],
      (row) => `${row.term} — ${row.scope || "unknown"} (${row.c})`)
  }

  // Why OTP logins failed over 7 days, from LiveActivitySnapshot.otp_causes.
  //
  // Each row carries the one number that makes its cause actionable rather
  // than just a count: a median age sitting just past 30 minutes says the
  // expiry window is too short, while rows where no code was ever issued say
  // people are typing a code for an address we never sent one to -- a
  // different problem with a different fix.
  //
  // Rows predating the diagnostics arrive as 'not_recorded'. They are shown,
  // not hidden, and the caveat states how many of the failures actually
  // carry a measured cause, so a table full of unmeasured rows cannot be
  // misread as a finding.
  renderOtpCauses(data) {
    const causes = (data || {}).causes || []
    const totals = (data || {}).totals || {}
    const failures = totals.failures || 0
    const recorded = totals.recorded || 0

    if (failures === 0) {
      this.otpCauseCaveatTarget.textContent = "No OTP failures in the past 7 days."
    } else if (recorded === 0) {
      this.otpCauseCaveatTarget.textContent =
        `${failures} failures, none with a recorded cause yet — the measurement ships with the next deploy.`
    } else {
      this.otpCauseCaveatTarget.textContent =
        `${recorded} of ${failures} failures have a measured cause; the rest predate the measurement.`
    }

    this.otpCausesTarget.innerHTML = ""
    causes.forEach((row) => {
      const tr = document.createElement("tr")
      // textContent per cell, never innerHTML: cause strings come from a
      // jsonb column and are not ours to trust blindly.
      const cells = [row.cause || "unknown", String(row.c || 0), this.otpCauseHint(row)]
      cells.forEach((value) => {
        const td = document.createElement("td")
        td.textContent = value
        tr.appendChild(td)
      })
      this.otpCausesTarget.appendChild(tr)
    })
  }

  // The reading of each cause, in words, so nobody has to remember which
  // column matters for which row.
  otpCauseHint(row) {
    const parts = []
    if (row.cause === "code_expired" && row.median_age_min !== null && row.median_age_min !== undefined) {
      parts.push(`median age ${row.median_age_min} min`)
      // 30 minutes is MagicLink#otp_not_expired?. Clustering just past it is
      // the signal that the window itself is the problem.
      if (row.median_age_min <= 45) { parts.push("just past the 30 min window") }
    }
    if (row.no_code_issued > 0) { parts.push(`${row.no_code_issued} never issued a code`) }
    if (row.not_numeric > 0) { parts.push(`${row.not_numeric} not a numeric code`) }
    if (row.cause === "not_recorded") { parts.push("logged before the measurement shipped") }
    return parts.length ? parts.join(", ") : "—"
  }

  // `baseline` is the median/min/max of the same 30-minute clock window, same
  // weekday, over the previous 4 weeks -- see LiveActivitySnapshot.baseline.
  // It is RAW (bots not excluded), so the only fair comparison is against a
  // RAW current count: last30 (already bot-filtered) plus bots_excluded puts
  // it back to the same raw shape as the baseline. Deliberately quiet --
  // small text, no colour-only signal (the above/below wording carries the
  // verdict as text, not as a colour an admin could miss) -- and the title
  // tooltip is the one place the raw-vs-filtered distinction is spelled out,
  // so the page itself stays uncluttered.
  renderBaseline(baseline, totals) {
    if (!baseline) {
      this.last30BaselineTarget.textContent = ""
      this.last30BaselineTarget.removeAttribute("title")
      return
    }
    const rawCurrent = (totals.last30 || 0) + (totals.bots_excluded || 0)
    let verdict = "within typical range"
    if (rawCurrent > baseline.max) verdict = "above typical"
    else if (rawCurrent < baseline.min) verdict = "below typical"

    this.last30BaselineTarget.textContent =
      `typical ${Math.round(baseline.median)} (${baseline.min}–${baseline.max}) — ${verdict}`
    this.last30BaselineTarget.title =
      "Baseline is TOTAL traffic (bots included), not the bot-filtered count above: " +
      "median, min and max of the same 30-minute window on the same weekday over the " +
      "previous 4 weeks. Compared here against last30 + bots excluded (also raw), " +
      "since historical rows predate bot classification and would misread as ~0 bots " +
      "if filtered."
  }

  // A dedicated renderer rather than renderList/formatter: the feed needs
  // several independently-aligned columns (time, type, platform, email,
  // company, detail, city), not one formatted string. Column widths are
  // fixed via inline style — never a computed class string — because
  // tailwind.config.js's theme.colors REPLACES the default palette with no
  // safelist, so a default Tailwind colour name silently emits no CSS.
  // Every value is set via textContent, never innerHTML: `detail` (and,
  // for the mobile source-tracking rows, the `source` value it carries) is
  // client-supplied, and `email`/`company_name` come from decrypted/joined
  // employee data the UI must not trust as markup either. `detail` is
  // the one open-ended column (it can be a full sentence or a stray email
  // address per Ruling R15) so it alone gets a CSS ellipsis instead of
  // wrapping, keeping every row a single line.
  renderFeed(rows) {
    this.feedTarget.innerHTML = ""
    rows.forEach((row) => {
      const item = document.createElement("li")
      item.style.cssText = "display:flex;gap:16px;padding:2px 0;align-items:baseline"

      const time = document.createElement("span")
      time.style.cssText = "flex:none;width:72px;font-variant-numeric:tabular-nums"
      time.textContent = row.time || "–"

      const type = document.createElement("span")
      type.style.cssText = "flex:none;width:200px"
      type.textContent = row.activity_type || "–"

      const platform = document.createElement("span")
      platform.style.cssText = "flex:none;width:64px"
      platform.textContent = row.platform || "–"

      // The four columns below are the ellipsis ones, and a long value in any
      // of them reads as "ali.mahmood.obaid.hassan.bin...." with no way to see
      // the rest. setTruncatedCell gives each the full value as a native title
      // tooltip.
      const email = document.createElement("span")
      email.style.cssText = "flex:none;width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
      this.setTruncatedCell(email, row.email, "–")

      const company = document.createElement("span")
      company.style.cssText = "flex:none;width:160px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
      this.setTruncatedCell(company, row.company_name, "–")

      const detail = document.createElement("span")
      detail.style.cssText = "flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
      this.setTruncatedCell(detail, row.detail, "–")

      const city = document.createElement("span")
      city.style.cssText = "flex:none;width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap"
      this.setTruncatedCell(city, row.geo_city, "unknown")

      item.append(time, type, platform, email, company, detail)

      // Per-row click affordance that opens the detail modal with the full
      // `hash_parameters` object for this row, one `key: value` line per
      // entry. Scope per the user's own words — "in the info we can list
      // all parameters for all activity types" — so this deliberately does
      // NOT filter out keys that duplicate a rendered column
      // (title/resource_name/platform/email/current_user all still appear
      // here too). buildInfoIcon returns null when there is nothing to
      // show, and in that case no icon is appended at all — an affordance
      // that opens an empty modal is worse than none.
      const info = this.buildInfoIcon(row)
      if (info) item.append(info)

      item.append(city)
      this.feedTarget.appendChild(item)
    })
  }

  // Writes a feed cell that may be cut off by text-overflow:ellipsis, and
  // hands the reader the whole value on hover through the native title
  // tooltip. No markup, no CSS and no library -- which also means it survives
  // a stale asset bundle, and it works on the columns that are already
  // ellipsised without changing the row layout at all.
  //
  // title is set unconditionally rather than only when the text actually
  // overflows. Detecting that means reading scrollWidth/clientWidth per cell,
  // which forces layout -- roughly 400 synchronous reflow reads every poll at
  // 100 rows x 4 cells, on a dashboard that repaints every few seconds. A
  // tooltip that repeats a fully visible value is a far smaller cost than
  // that.
  //
  // The placeholder ("–" / "unknown") never becomes a tooltip: hovering a dash
  // to be told it is a dash is noise.
  setTruncatedCell(element, value, fallback) {
    element.textContent = value || fallback
    if (value) {
      element.title = value
    } else {
      element.removeAttribute("title")
    }
  }

  // hash_parameters arrives as a JSON object (or null/absent, e.g.
  // sign_in_page_visited, which never sets any). Null-valued keys are
  // dropped rather than rendered as "key: –": applink_opened has rows
  // where resource/resource_id/resource_name are ALL null, and after
  // dropping null values those rows correctly produce zero lines, which
  // this method treats the same as "no hash_parameters" — no icon. Built
  // with textContent only, never innerHTML: values can be raw emails or
  // free text (per Ruling R15) and must stay XSS-safe.
  //
  // The click handler below captures `time`, `activityType` and `lines` as
  // plain values INTO the closure right now, rather than holding a
  // reference to `row` or to this <li> to re-read later: renderFeed()
  // rebuilds the whole feed list every ~5s poll, so this icon (and the row
  // it belongs to) will be gone from the DOM within one tick. By the time
  // the admin clicks it, the modal must open with the data as it was AT
  // CLICK TIME, not whatever the feed has since become.
  buildInfoIcon(row) {
    const hashParameters = row.hash_parameters
    if (!hashParameters || typeof hashParameters !== "object") return null

    const lines = Object.entries(hashParameters)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => [key, typeof value === "object" ? JSON.stringify(value) : String(value)])
    if (lines.length === 0) return null

    const icon = document.createElement("span")
    icon.style.cssText = "flex:none;cursor:pointer"
    icon.textContent = "ⓘ"
    icon.title = "Details" // short static label only — the parameters themselves live in the modal now.

    const time = row.time || "–"
    const activityType = row.activity_type || "–"
    icon.addEventListener("click", () => this.openModal(time, activityType, lines))
    return icon
  }

  // Renders the row context (time, activity type) plus every hash_parameters
  // entry as a selectable `key: value` line, so a resource id or email can
  // be copied straight out of the modal — the whole reason this replaces a
  // hover tooltip. `lines` is the array of [key, value] string pairs
  // captured by buildInfoIcon at click time, not re-read from anywhere.
  openModal(time, activityType, lines) {
    this.modalMetaTarget.textContent = `${time} — ${activityType}`

    this.modalLinesTarget.innerHTML = "" // clearing the container we are about to rebuild, same idiom as renderList/renderFeed — not a value.
    lines.forEach(([key, value]) => {
      const dt = document.createElement("dt")
      dt.style.cssText = "font-weight:600;margin-top:8px"
      dt.textContent = key

      const dd = document.createElement("dd")
      dd.style.cssText = "margin:0 0 4px;word-break:break-word;user-select:text"
      dd.textContent = value

      this.modalLinesTarget.append(dt, dd)
    })

    this.modalTarget.classList.remove("hidden")
  }

  closeModal() {
    this.modalTarget.classList.add("hidden")
  }

  // Document-level so Escape works regardless of what has focus; see the
  // listener added/removed in connect()/disconnect().
  handleKeydown(event) {
    if (event.key !== "Escape") return
    if (this.modalTarget.classList.contains("hidden")) return
    this.closeModal()
  }

  // Diff rather than recreate, so pan and zoom survive each tick. Also
  // collects which cities INCREASED since the last tick, so the ripple can
  // be fired on the biggest jumps (capped at RIPPLE_CAP) once the diff pass
  // is complete.
  syncMarkers(cities) {
    if (!this.map) return // Maps failed to load; nothing to sync against.
    const seen = new Set()
    const increases = []
    cities.forEach((city) => {
      const key = `${city.geo_city}|${city.geo_country}`
      seen.add(key)
      const count = Number(city.c) || 0
      const diameter = (4 + Math.sqrt(count) * 2) * 2
      const position = new google.maps.LatLng(Number(city.lat), Number(city.lng))
      const existing = this.markers.get(key)
      if (existing) {
        // lat/lng are avg(geo_lat)/avg(geo_lng) over the rolling 30-minute
        // window, so the centroid drifts tick to tick — update position AND
        // size, not just the icon, or a long-lived marker goes silently stale.
        const increase = count - existing.lastCount
        if (increase > 0) increases.push({ overlay: existing, increase })
        existing.lastCount = count
        existing.update(position, diameter, MARKER_COLOR, `${city.geo_city}: ${count}`)
      } else {
        const overlay = new (cityMarkerOverlay())(position, diameter, MARKER_COLOR, `${city.geo_city}: ${count}`)
        overlay.lastCount = count
        overlay.setMap(this.map)
        this.markers.set(key, overlay)
        // A brand-new city is itself a count increase (0 -> count).
        if (count > 0) increases.push({ overlay, increase: count })
      }
    })
    this.markers.forEach((overlay, key) => {
      if (!seen.has(key)) {
        overlay.setMap(null) // OverlayView equivalent of marker.setMap(null) — detaches the DOM node.
        this.markers.delete(key)
      }
    })

    increases
      .sort((a, b) => b.increase - a.increase)
      .slice(0, RIPPLE_CAP)
      .forEach(({ overlay }) => overlay.triggerRipple())
  }

  renderList(target, rows, formatter, extraRow) {
    target.innerHTML = ""
    rows.forEach((row) => {
      const item = document.createElement("li")
      item.textContent = formatter(row)
      target.appendChild(item)
    })
    if (extraRow) {
      const item = document.createElement("li")
      item.textContent = extraRow
      target.appendChild(item)
    }
  }

  showBanner(message) {
    this.bannerTarget.textContent = message
    this.bannerTarget.classList.remove("hidden")
  }

  hideBanner() {
    // The map being unavailable is a persistent truth, and ticks keep
    // succeeding (or recovering from backoff) afterwards — each one would
    // otherwise call hideBanner() and either wipe the warning (prior bug) or
    // (if merely no-opping) leave whatever text some OTHER showBanner call
    // (e.g. "index_pending", or a degraded-connectivity backoff message)
    // last left behind, stranding that stale message forever. Re-assert the
    // correct text instead of trusting leftovers.
    if (this.mapUnavailable) {
      this.showBanner(MAP_UNAVAILABLE_MESSAGE)
      return
    }
    this.bannerTarget.classList.add("hidden")
  }
}
